import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ExternalLinkWarningProps {
  url: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const STORAGE_KEY = 'discoverant-external-link-trusted-sites';

function getTrustedSites(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function addTrustedSite(hostname: string) {
  const sites = getTrustedSites();
  sites.add(hostname);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(sites)));
}

export function isTrustedSite(url: string): boolean {
  try {
    const hostname = new URL(url).hostname;
    return getTrustedSites().has(hostname);
  } catch {
    return false;
  }
}

/**
 * Opens an external URL, showing a warning dialog unless the site is trusted.
 * Returns true if the link was opened immediately (trusted), false if dialog shown.
 */
export function openExternalLink(
  url: string,
  setWarning: (state: { url: string; isOpen: boolean }) => void,
): boolean {
  if (isTrustedSite(url)) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  }
  setWarning({ url, isOpen: true });
  return false;
}

const ExternalLinkWarning: React.FC<ExternalLinkWarningProps> = ({
  url,
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [dontWarn, setDontWarn] = useState(false);

  useEffect(() => {
    if (isOpen) setDontWarn(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  let hostname = '';
  try { hostname = new URL(url).hostname; } catch { /* */ }

  const handleConfirm = () => {
    if (dontWarn && hostname) {
      addTrustedSite(hostname);
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    onConfirm();
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 100000, backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="rounded-xl shadow-2xl w-full max-w-md mx-4"
        style={{ backgroundColor: 'var(--card-bg, #1e1e2e)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5">
          {/* Warning icon */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'rgba(234,179,8,0.15)' }}>
              <svg className="w-5 h-5" style={{ color: '#eab308' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
              You are leaving Adroit
            </h3>
          </div>

          <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
            This link will take you to an external website outside the Adroit platform.
          </p>

          {/* URL display */}
          <div className="px-3 py-2 rounded-lg mb-3 overflow-x-auto"
            style={{ backgroundColor: 'var(--content-bg, #141420)', border: '1px solid var(--card-border, #333)' }}>
            <code className="text-xs break-all" style={{ color: 'var(--text-muted)', fontFamily: 'monospace' }}>
              {url}
            </code>
          </div>

          <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
            Adroit cannot guarantee the security or availability of external sites.
          </p>

          {/* Don't warn checkbox */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontWarn}
              onChange={(e) => setDontWarn(e.target.checked)}
              className="w-3.5 h-3.5 rounded accent-teal-500"
            />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Don't warn me again for {hostname || 'this site'}
            </span>
          </label>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t" style={{ borderColor: 'var(--card-border, #e5e7eb)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg transition-colors"
            style={{
              color: 'var(--text-secondary)',
              border: '1px solid var(--card-border, #555)',
              backgroundColor: 'transparent',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
            style={{ backgroundColor: '#0d9488' }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#0f766e'; }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = '#0d9488'; }}
          >
            Open External &#8599;
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ExternalLinkWarning;
